/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useCallback, useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFormDraft } from '@/shared/hooks/use-form-draft';
import { useDraftUserId } from '@/shared/hooks/use-draft-user-id';
import type { PropertyFormValues } from './schema';

export interface UsePropertyDraftProps {
  form: UseFormReturn<PropertyFormValues>;
  agencyId: string;
  editingProperty?: Record<string, any> | null;
  defaultValues: PropertyFormValues;
  open: boolean;
}

/**
 * Local draft of the property form: new property = key `new`, edit = the
 * property id. Photos/videos/documents live outside the form values and are
 * never stored.
 *
 * @param props - The form, scope and open state.
 * @returns The offer and the restore/discard/clear/flush actions.
 */
export function usePropertyDraft(props: UsePropertyDraftProps) {
  const { form, agencyId, editingProperty, defaultValues, open } = props;
  const userId = useDraftUserId();
  const serverUpdatedAt = editingProperty?.metadata?.updated_at ?? editingProperty?.updated_at ?? null;
  const draft = useFormDraft<PropertyFormValues>({
    scope: { userId, agencyId, kind: 'property', id: editingProperty?.id },
    initial: defaultValues,
    active: open,
    serverUpdatedAt,
    getCurrent: form.getValues,
  });
  const { schedule, flush, clear, discard, restore: takeDraft } = draft;

  useEffect(() => {
    const subscription = form.watch((values) => schedule(values as PropertyFormValues));
    return () => subscription.unsubscribe();
  }, [form, schedule]);

  const restore = useCallback(() => {
    const data = takeDraft();
    if (!data) return;
    Object.entries(data).forEach(([name, value]) => {
      form.setValue(name as keyof PropertyFormValues, value as never, { shouldDirty: true });
    });
  }, [form, takeDraft]);

  const keep = useCallback(() => flush(form.getValues()), [flush, form]);

  return { offer: draft.offer, restore, discard, clear, keep };
}
